import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert,
  Stack, Divider, CircularProgress,
} from '@mui/material';
import { Park, AdminPanelSettings } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';

export default function Login() {
  const { login, setup } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'login' | 'setup' | 'loading'>('loading');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Setup-Status beim Mount prüfen
  useEffect(() => {
    authAPI.setupStatus()
      .then((s) => setMode(s.needsSetup ? 'setup' : 'login'))
      .catch(() => setMode('login'));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (mode === 'login') {
        await login(email, password);
      } else if (mode === 'setup') {
        if (!name.trim()) {
          setError('Name erforderlich');
          setBusy(false);
          return;
        }
        await setup(email, password, name);
      }
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Vorgang fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'loading') {
    return (
      <Box sx={{
        minHeight: '100vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        bgcolor: 'background.default',
      }}>
        <CircularProgress />
      </Box>
    );
  }

  const isSetup = mode === 'setup';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 420, width: '100%' }}>
        <Stack alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <Box sx={{
            width: 64, height: 64, borderRadius: '50%',
            bgcolor: 'primary.main', color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Park sx={{ fontSize: 40 }} />
          </Box>
          <Typography variant="h5" fontWeight={700} sx={{ mt: 1.5 }}>
            Baumwächter
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Erfassung
          </Typography>
        </Stack>

        {isSetup && (
          <Alert
            icon={<AdminPanelSettings />}
            severity="info"
            sx={{ mb: 2 }}
          >
            <strong>Erster Start.</strong> Lege den Administrator-Account an.
            Weitere User werden nur über die Admin-Verwaltung angelegt.
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {isSetup && (
              <TextField
                label="Name"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                autoComplete="name"
              />
            )}
            <TextField
              label="E-Mail"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus={!isSetup}
              autoComplete={isSetup ? 'email' : 'username'}
              required
            />
            <TextField
              label="Passwort"
              type="password"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={isSetup ? 'new-password' : 'current-password'}
              helperText={isSetup ? 'Mindestens 8 Zeichen' : undefined}
              required
            />
            {error && <Alert severity="error">{error}</Alert>}
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={busy}
              fullWidth
            >
              {busy ? 'Bitte warten…' : isSetup ? 'Admin anlegen' : 'Anmelden'}
            </Button>
          </Stack>
        </form>

        {!isSetup && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Kein Account? Frag den Administrator nach einem Zugang.
              </Typography>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}
